import { gql, useLazyQuery } from '@apollo/client'
import { Query } from '@promoboxx/graphql-gateway-types'
import { memo, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  RETAILER_ACCOUNT_FIELDS,
  RETAILER_ACCOUNT_FIELDS_FRAGMENT,
} from '@src/components/auth/saneLogin'
import useCurrentAuth from '@src/lib/useCurrentAuth'
import useIsCurrentUserInternal from '@src/lib/useIsCurrentUserInternal'
import useRetailerAccountSlugFromRoute from '@src/lib/useRetailerAccountSlugFromRoute'
import { selectRetailerAccount } from '@src/stores/app/actions'

const RetailerAccountSelectorHandler: React.FC<React.PropsWithChildren> = (
  props,
) => {
  const { retailerAccounts } = useCurrentAuth()
  const retailerAccountSlug = useRetailerAccountSlugFromRoute()
  const [done, setDone] = useState(false)
  const dispatch = useDispatch()
  const isUserInternal = useIsCurrentUserInternal()
  const [getRetailerAccount] = useLazyQuery<Query>(QUERY)
  const isFetchingRef = useRef(false)

  useLayoutEffect(() => {
    async function run() {
      if (isFetchingRef.current) {
        return
      }

      isFetchingRef.current = true

      const foundRetailerAccount = retailerAccounts.find(
        (x) => x.slug === retailerAccountSlug,
      )

      if (foundRetailerAccount) {
        setDone(false)

        dispatch(
          selectRetailerAccount({
            id: Number(foundRetailerAccount.id),
            skipUrlUpdate: true,
          }),
        )

        setDone(true)
      } else if (retailerAccountSlug && isUserInternal) {
        setDone(false)

        const { data } = await getRetailerAccount({
          variables: {
            id: retailerAccountSlug,
            fields: RETAILER_ACCOUNT_FIELDS,
          },
        })

        if (data?.getRetailerAccount) {
          dispatch({
            type: 'GET_RETAILER_ACCOUNT_FULFILLED',
            payload: {
              response: data.getRetailerAccount,
            },
            meta: {
              ...data.getRetailerAccount,
            },
          })

          dispatch(
            selectRetailerAccount({
              id: Number(data.getRetailerAccount.id),
              skipUrlUpdate: true,
            }),
          )

          setDone(true)
        }
      } else {
        // TODO What goes here? There's no slug, the user isn't internal, hm
      }

      isFetchingRef.current = false
    }

    run()
  }, [
    dispatch,
    isUserInternal,
    retailerAccountSlug,
    retailerAccounts,
    getRetailerAccount,
  ])

  return done ? props.children : null
}

const QUERY = gql`
  ${RETAILER_ACCOUNT_FIELDS_FRAGMENT}
  query getRetailerAccountInternal($id: ID!, $fields: [String!]) {
    getRetailerAccount(id: $id, fields: $fields) {
      ...RetailerAccountFieldsFragment
    }
  }
`

export default memo(RetailerAccountSelectorHandler)
